"use client";
// Force re-evaluation

import type { ReactNode } from "react"
import { createContext, useContext, useState, useEffect } from "react"
import type { Locale } from "./i18n"
import { defaultLocale, getTranslation } from "./i18n"

interface LocaleContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
  dir: "ltr" | "rtl"
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(defaultLocale)

  useEffect(() => {
    // Load locale from localStorage
    const savedLocale = localStorage.getItem("locale") as Locale
    if (savedLocale && (savedLocale === "en" || savedLocale === "ar" || savedLocale === "ur")) {
      setLocaleState(savedLocale)
    } else {
      localStorage.setItem("locale", defaultLocale)
      localStorage.setItem("i18nextLng", defaultLocale)
    }
  }, [])

  useEffect(() => {
    // Update document direction and lang
    const dir = locale === "ar" || locale === "ur" ? "rtl" : "ltr"
    document.documentElement.setAttribute("dir", dir)
    document.documentElement.setAttribute("lang", locale)
  }, [locale])

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale)
    localStorage.setItem("locale", newLocale)
    localStorage.setItem("i18nextLng", newLocale)
  }

  const t = (key: string) => getTranslation(locale, key)

  const dir = locale === "ar" || locale === "ur" ? "rtl" : "ltr"

  return <LocaleContext.Provider value={{ locale, setLocale, t, dir }}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (context === undefined) {
    throw new Error("useLocale must be used within a LocaleProvider")
  }
  return context
}
